import ScopeChangeRequestStore from 'admin/ScopeChangeRequestStore'
import { TableSortOrder } from 'components/Table/ClickableTable'
import Workspace from 'iam/models/Workspace'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import Job from 'jobs/Job'
import once from 'lodash/once'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import ScopeChangeRequest from './ScopeChangeRequest'
import ScopeChangeRequestsSearchViewModel from './ScopeChangeRequestsSearchViewModel'
import {
  ScopeChangeRequestColumnDetails,
  ScopeChangeRequestTableColumns,
} from './ScopeChangeRequestsTableColumn'
import ScopeChangeRequestDetailsViewModel from './components/ScopeChangeRequestDetailsPanel/ScopeChangeRequestDetailsViewModel'
import {
  GetCurrentUserWorkspaceMemberships,
  FetchUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import ScopeChangeRequestFinalizedDialogViewModel from './views/ScopeChangeRequestFinalizedDialog/ScopeChangeRequestFinalizedDialogViewModel'
import { GetCurrentMember } from 'iam/SessionStore'
import ScopeChangeRequestResolveDialogViewModel from './views/ScopeChangeRequestResolveDialog/ScopeChangeRequestResolveDialogViewModel'
import ScopeChangeRequestRejectDialogViewModel from './views/ScopeChangeRequestRejectDialog/ScopeChangeRequestRejectDialogViewModel'
import ScopeChangeRequestRejectOnBehalfOfClientViewModel from './views/ScopeChangeRequestRejectOnBehalfOfClientDialog/ScopeChangeRequqestRejectOnBehalfOfClientViewModel'
import { AlertFactory } from 'alerts/Alert'
import InventoryStore from 'admin/InventoryStore'

export type FetchJobs = (ids: string[]) => Promise<Job[]>

/**
 * Workspace filter for Scope Change Requests.
 */
export type ScopeChangeRequestsWorkspaceFilter =
  | AllWorkspaces
  | WorkspaceScopeChangeRequests

/**
 * Scope change requests in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * Scope change requests in a workspace.
 */
export interface WorkspaceScopeChangeRequests {
  type: 'Workspace'
  workspace: Workspace
}

export default class ScopeChangeRequestsViewModel {
  searcher = new ScopeChangeRequestsSearchViewModel(
    this.scopeChangeRequestStore,
    this.fetchWorkspaceMemberships,
    this.getWorkspaceMemberships,
    this.fetchJobs,
    this.createFilterStorage('ChangeRequests'),
  )

  scopeChangeRequestFinalizedDialogVM =
    new ScopeChangeRequestFinalizedDialogViewModel()

  scopeChangeRequestRejectOnBehalfOfClientDialogVM =
    new ScopeChangeRequestRejectOnBehalfOfClientViewModel(
      this.scopeChangeRequestStore,
      this.refreshStatusFilter.bind(this),
    )

  scopeChangeRequestResolveDialogVM =
    new ScopeChangeRequestResolveDialogViewModel(
      this.scopeChangeRequestStore,
      this.scopeChangeRequestRejectOnBehalfOfClientDialogVM,
      this.refreshStatusFilter.bind(this),
    )

  scopeChangeRequestRejectDialogVM =
    new ScopeChangeRequestRejectDialogViewModel(
      this.scopeChangeRequestStore,
      this.refreshStatusFilter.bind(this),
    )

  changeRequestDetailsVM = new ScopeChangeRequestDetailsViewModel(
    this.inventoryStore,
    this.scopeChangeRequestStore,
    this.scopeChangeRequestFinalizedDialogVM,
    this.scopeChangeRequestResolveDialogVM,
    this.scopeChangeRequestRejectDialogVM,
    this.getCurrentMember,
    this.refreshStatusFilter.bind(this),
    this.closeNav,
    this.newAlert,
  )

  /**
   * Columns to populate the scope change requests table header and rows with.
   */
  @observable columns = ScopeChangeRequestTableColumns

  /**
   * Whether all requests are selected
   */
  @observable allSelected = false

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of scope change requests.
   */
  private activateSearcher = once(() => this.searcher.activate())

  constructor(
    private inventoryStore: InventoryStore,
    private scopeChangeRequestStore: ScopeChangeRequestStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private createFilterStorage: FilterStorageFactory,
    private fetchJobs: FetchJobs,
    private getCurrentMember: GetCurrentMember,
    private closeNav: () => void,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Workspace filter.
   */
  @observable workspaceFilter: ScopeChangeRequestsWorkspaceFilter = {
    type: 'AllWorkspaces',
  }

  /**
   * Filtered scope change requests
   */
  @computed
  get filteredItems() {
    return this.searcher.filteredItems
  }

  /**
   * Refresh status filter
   * @returns
   */
  refreshStatusFilter() {
    return this.searcher.refreshStatusFilter()
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'Workspace' }
  }

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   * @returns
   */
  getSortOrder(column: ScopeChangeRequestColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   * @returns
   */
  setSortOrder(
    column: ScopeChangeRequestColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Shows a request in the detail panel
   * @param changeRequest
   */
  @action.bound
  selectChangeRequest(changeRequest: ScopeChangeRequest) {
    this.changeRequestDetailsVM.show(changeRequest)
  }

  activate = task.resolved(async () => {
    await this.activateSearcher()
  })
}
